import { ApiResponse } from '../types';
import { FulfilmentOptionsRequest, FulfilmentOptionsResponse, InternalFulfilmentV3OptionsResponse } from './types';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { generateRecaptchaHeaders } from '../utils/recaptcha/generateRecaptchaHeaders';
import { ApiClient } from '../client/ApiClient';

export const FULFILMENT_API_RECAPTCHA_ACTION = 'fulfilmentApi';

export class FulfilmentClient extends ApiClient {
  private readonly _fulfillmentBaseUrl: string;
  private readonly _fulfillmentOptionsUrl: string;
  private readonly _apiKey?: string;
  private readonly _countryCode: CountryCode;

  constructor({
    fulfillmentBaseUrl,
    fulfillmentOptionsUrl = '/FulfilmentOptions',
    apiKey,
    countryCode = 'AU',
  }: {
    fulfillmentBaseUrl: string;
    fulfillmentOptionsUrl?: string;
    fulfillmentOptionsValidateUrl?: string;
    apiKey?: string;
    countryCode?: CountryCode;
  }) {
    super();
    this._fulfillmentBaseUrl = fulfillmentBaseUrl;
    this._fulfillmentOptionsUrl = fulfillmentOptionsUrl;
    this._apiKey = apiKey;
    this._countryCode = countryCode;
  }

  override get requestHeaders(): Record<string, string> {
    return {
      ...super.requestHeaders,
      ...getMultiTenancyHeaders(this._countryCode),
      ...(this._apiKey ? { 'Ocp-Apim-Subscription-Key': this._apiKey } : {}),
    };
  }

  getOptions = async (
    body: FulfilmentOptionsRequest,
    recaptchaToken: string
  ): Promise<ApiResponse<FulfilmentOptionsResponse>> => {
    const { data, statusCode, error } = await this._fetch<InternalFulfilmentV3OptionsResponse>({
      path: `${this._fulfillmentBaseUrl}${this._fulfillmentOptionsUrl}`,
      method: 'POST',
      body,
      headers: { ...generateRecaptchaHeaders(recaptchaToken, FULFILMENT_API_RECAPTCHA_ACTION) },
    });

    if (error) {
      return { error, statusCode };
    }

    /* Checking for errors only in the delivery payload to see if the whole product has gone wrong
    /* but to avoid causing an issue where ClickAndCollect has an error but Delivery doesn't
    /* TODO: Move this error-handling down the chain to make it independent of fulfilment type */
    const wasApiError = Boolean(data?.delivery?.presentation?.find(pres => pres?.type === 'Error'));

    if (wasApiError) {
      return { error: { message: 'Internal API Error' }, statusCode: 500 };
    }

    const processedData: FulfilmentOptionsResponse = {
      ClickAndCollect: data?.clickAndCollect,
      CashAndCarry: data?.cashAndCarry,
      Delivery: data?.delivery,
    };

    return { data: processedData, statusCode };
  };
}
